import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LayoutDashboard, Palette, FileText, LogOut, Settings, ChevronDown, User, Code } from 'lucide-react';

export default function TailspinHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    const adminRestored = await logout();
    // If admin session was restored, navigate to admin dashboard
    // Otherwise, navigate to login
    if (adminRestored) {
      navigate('/admin/dashboard');
    } else {
      navigate('/login');
    }
  };

  const menuItems = [
    {
      icon: LayoutDashboard,
      label: 'Dashboard',
      path: '/tailspin/dashboard',
      onClick: () => navigate('/tailspin/dashboard')
    },
    {
      icon: Palette,
      label: 'Brand Assets',
      path: '/tailspin/brand-assets',
      onClick: () => navigate('/tailspin/brand-assets')
    },
    {
      icon: FileText,
      label: 'Generate Collateral',
      path: '/tailspin/collateral',
      onClick: () => navigate('/tailspin/collateral')
    },
    {
      icon: Code,
      label: 'Generate HTML Collateral',
      path: '/tailspin/html-collateral',
      onClick: () => navigate('/tailspin/html-collateral')
    },
    ...(user?.role === 'superadmin' ? [{
      icon: Settings,
      label: 'Admin',
      path: '/admin/dashboard',
      onClick: () => navigate('/admin/dashboard')
    }] : []),
    {
      icon: LogOut,
      label: 'Logout',
      onClick: handleLogout,
      separator: true
    }
  ];

  if (!user) {
    return null;
  }

  return (
    <header 
      className="sticky top-0 z-50 border-b"
      style={{ 
        backgroundColor: '#FFFFFF',
        borderColor: '#D7B472'
      }}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo on the left */}
          <button
            onClick={() => navigate('/tailspin/dashboard')}
            className="flex items-center focus:outline-none"
          >
            <img 
              src="https://i.ibb.co/MvVKzjj/file-00000000a5dc7209bf802deb262dc9a8.png"
              alt="Company Logo"
              className="h-12 w-auto cursor-pointer transition-opacity hover:opacity-80"
              onError={(e) => {
                // Fallback if image fails to load
                e.target.style.display = 'none';
              }}
            />
          </button>

          {/* User button on the right */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center gap-3 px-4 py-2 rounded-lg transition-colors"
              style={{
                backgroundColor: isDropdownOpen ? 'rgba(255, 107, 90, 0.1)' : 'transparent',
                color: '#0A0A10'
              }}
              onMouseEnter={(e) => {
                if (!isDropdownOpen) {
                  e.currentTarget.style.backgroundColor = 'rgba(215, 180, 114, 0.1)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isDropdownOpen) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div className="flex items-center gap-3">
                <div className="flex flex-col items-end">
                  <span className="text-sm font-semibold" style={{ color: '#0A0A10' }}>
                    {user.name || 'User'}
                  </span>
                  <span className="text-xs" style={{ color: '#0A0A10', opacity: 0.7 }}>
                    {user.email || ''}
                  </span>
                </div>
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(255, 107, 90, 0.15)' }}
                >
                  <User className="w-5 h-5" style={{ color: '#FF6B5A' }} />
                </div>
                <ChevronDown 
                  className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  style={{ color: '#0A0A10' }}
                />
              </div>
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div
                className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg border overflow-hidden"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderColor: '#D7B472'
                }}
              >
                {menuItems.map((item, index) => (
                  <div key={index}>
                    {item.separator && (
                      <div className="my-1" style={{ borderTop: '1px solid #D7B472', opacity: 0.3 }} />
                    )}
                    <button
                      onClick={() => {
                        item.onClick();
                        setIsDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left transition-colors"
                      style={{
                        color: '#0A0A10',
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 107, 90, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <item.icon className="w-5 h-5" style={{ color: '#FF6B5A' }} />
                      <span className="font-medium">{item.label}</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}

